import { AxiosResponse } from "axios"
import { useApiCommand } from "hooks/core/useApiCommand"
import {
  extractSourceCodes,
  fillExportMap,
  saveSourceCodes,
  getSourceCodes,
  ExtractSourceCodesResponse,
  FillExportMapResponse,
} from "api/AI/exportMapFill"
import {
  SourceCodeInventory,
  ExportMapFillOptions,
} from "types/sourceCodes.types"

/**
 * Return type interface for the useExportMapFill hook
 */
interface UseExportMapFillReturn {
  /** Extract the source code inventory from a DOCX (200 body, or 202 job id) */
  extractFromDocument: (
    file: File,
    isAsync: boolean
  ) => Promise<AxiosResponse<ExtractSourceCodesResponse | string>>
  isExtracting: boolean

  /** Match an export map CSV against an inventory (200 body, or 202 job id) */
  fillFromInventory: (
    exportMapCsv: string,
    inventory: SourceCodeInventory,
    options: ExportMapFillOptions,
    isAsync: boolean
  ) => Promise<AxiosResponse<FillExportMapResponse | string>>
  isFilling: boolean

  /** Store an inventory with the project */
  saveInventory: (
    projectId: string,
    inventory: SourceCodeInventory
  ) => Promise<AxiosResponse<unknown>>
  isSavingInventory: boolean

  /** Load the inventory stored with the project (null when none) */
  loadInventory: (projectId: string) => Promise<SourceCodeInventory | null>
  isLoadingInventory: boolean
}

/**
 * Custom hook for filling an export map from a source document.
 *
 * Wraps the AI-converter's extract / fill endpoints and the project-side
 * inventory store as imperative commands with pending flags.
 *
 * @example
 * const { extractFromDocument, fillFromInventory } = useExportMapFill()
 * const extracted = await awaitAsyncCapable(await extractFromDocument(file, true))
 */
export const useExportMapFill = (): UseExportMapFillReturn => {
  const [extractFromDocument, isExtracting] = useApiCommand(extractSourceCodes)
  const [fillFromInventory, isFilling] = useApiCommand(fillExportMap)
  const [saveInventory, isSavingInventory] = useApiCommand(saveSourceCodes)
  const [loadInventory, isLoadingInventory] = useApiCommand(getSourceCodes)

  return {
    extractFromDocument,
    isExtracting,
    fillFromInventory,
    isFilling,
    saveInventory,
    isSavingInventory,
    loadInventory,
    isLoadingInventory,
  }
}
